/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useContext, useState, useEffect, useRef } from 'react'
import { useServiceContext } from '../../root'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { useNotificationCount } from '../../hooks/useNotifications'
import {
  TableMultiSelectionWrapper,
  SelectAllRows,
  SelectRow,
  TableMultiSelectionContext
} from '../../components/rcm-table/TableMultiSelection'
import { assembleFiltersFromFormValues } from './DataService'
import { countAll } from '../../services/factory/notificationsFactory'
import TableMenu from './TableMenu'
import ReadNotification from './read/ReadNotification'
import { useQueryClient } from '@tanstack/react-query'
import { formatUserDateTime } from '~/utils/dateFormatting'

interface NotificationRow {
  id: string
  subject: string
  timeSent: string
  seen: boolean
  [key: string]: any
}

interface NotificationTableProps {
  [key: string]: any
}

const NotificationTable: React.FC<NotificationTableProps> = ({ ...props }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const getTotalCount = async () => {
    const result = await countAll()
    return (result as { count: number })?.count || 0
  }

  return (
    <div>
      <TableMultiSelectionWrapper
        texts={{
          selectAllItemsText: 'NOTIFICATIONS_MULTISELECT_SELECT_ALL_IN_SECTION',
          allItemsSelectedText: 'NOTIFICATIONS_MULTISELECT_ALL_N_ITEMS_SELECTED'
        }}
        getTotalNumberFunction={getTotalCount}
        assembleFilterFunction={assembleFiltersFromFormValues}
      >
        <NotificationTableContent {...props} />
      </TableMultiSelectionWrapper>
    </div>
  )
}

const NotificationTableContent: React.FC<NotificationTableProps> = ({ ...props }) => {
  const context = useServiceContext()
  const queryClient = useQueryClient()
  const tableData = useRcmTableData()
  const [showReadModal, setShowReadModal] = useState(false)
  const [onSubjectClickDetails, setOnSubjectClickDetails] = useState<NotificationRow>({} as NotificationRow)
  const { translate } = context.service.filter
  const multiSelectionContext = useContext(TableMultiSelectionContext)

  // Watch notification count and refresh table when it changes
  const { count } = useNotificationCount()
  const previousCountRef = useRef<number>(count)

  useEffect(() => {
    // Skip the first render
    if (previousCountRef.current !== undefined && previousCountRef.current !== count) {
      // Notification count changed, refresh the table
      tableData.refreshTable()
    }
    previousCountRef.current = count
  }, [count, tableData])

  const emitNotificationsUpdate = () => {
    window.dispatchEvent(new CustomEvent('updateNotificationsCount'))
    window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
    window.dispatchEvent(new CustomEvent('updateNotifications'))
    // Force refetch of all notification-related queries
    queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
    queryClient.invalidateQueries({ queryKey: ['notification-count'] })
    queryClient.invalidateQueries({ queryKey: ['ws-history-types'] })
  }

  return (
    <>
      <RcmTable
        className="chita-table"
        simplePaginate={true}
        HeadRow={
          <TableHeadRow className="has-actions">
            <th className="clipped-cell with-checkbox" id="e0d5a16b-bbce-4377-abd4-bfe88c89cc80">
              <SelectAllRows />
            </th>
            <TableHeadCell id="35b85cae-cce4-4b97-a51f-6ce1e6e0361e" style={{ fontSize: '14px' }} sort="subject">
              {translate('NOTIFICATIONS_TH_SUBJECT')}
            </TableHeadCell>
            <TableHeadCell id="b167ebad-3e6f-44a0-bd54-0ac247ae3929" style={{ fontSize: '14px' }} sort="timeCreated">
              {translate('NOTIFICATIONS_TH_DATE_SENT')}
            </TableHeadCell>
            <TableHeadCell id="1460a3e9-5ea1-4b5c-8d3c-d258bbc5d592" className="actions">
              <span className="fs14">{translate('ACTIONS')}</span>
            </TableHeadCell>
          </TableHeadRow>
        }
        Row={({ row, ...rowProps }: { row: NotificationRow; [key: string]: any }) => {
          const flow = row

          return (
            <TableRow {...rowProps}>
              <td className="clipped-cell with-checkbox">
                <SelectRow
                  row={flow}
                  disabled={false}
                  allSelected={multiSelectionContext?.multiSelectState?.selectedWholeResult}
                />
              </td>
              <td>
                <button
                  type="button"
                  className={!flow.seen ? 'font-weight-bold btn-link' : ' btn-link'}
                  title={flow.subject}
                  onClick={() => {
                    setShowReadModal(true)
                    setOnSubjectClickDetails(flow)
                  }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                >
                  {flow.subject}
                </button>
              </td>
              <td style={{ fontSize: '14px' }}>{formatUserDateTime(flow.timeSent)}</td>
              <td className="actions" style={{ fontSize: '14px' }}>
                <TableMenu
                  item={flow}
                  options={{
                    selectedIds: multiSelectionContext?.multiSelectState.selectedIds || [],
                    selectedWholeResult: multiSelectionContext?.multiSelectState.selectedWholeResult
                  }}
                />
              </td>
            </TableRow>
          )
        }}
        {...props}
      />
      {showReadModal && (
        <ReadNotification
          notification={onSubjectClickDetails as any}
          onClose={() => {
            setShowReadModal(false)
            setOnSubjectClickDetails({} as NotificationRow)
            emitNotificationsUpdate()
          }}
        />
      )}
    </>
  )
}

export default NotificationTable
